import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

export default function ArticlePage() {
  const { id: slug } = useParams<{ id: string }>();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
          <p className="mt-4 text-foreground-500">文章未找到</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id).slice(0, 3);
  const imgSrc = articleImages[article.slug] || '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://jiuqu.sese/article/${article.slug}`,
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <SeoHead title={article.title} description={article.excerpt} keywords={`就去色色,${article.tags.join(',')},${article.categoryName}`} canonicalUrl={`/article/${article.slug}`} ogType="article" jsonLd={jsonLd} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <article className="pt-20 pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' },{ label: article.categoryName, href: `/category/${article.categorySlug}` },{ label: article.title }]} />

            <div className="mt-8">
              <span className="text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1 rounded-full whitespace-nowrap">{article.categoryName}</span>
              <h1 className="mt-5 text-2xl md:text-4xl font-heading text-foreground-900 leading-tight">{article.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span>{article.author}</span>
                <span>{article.date}</span>
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </div>

            {imgSrc && (
              <div className="mt-8 w-full rounded-xl overflow-hidden">
                <img src={imgSrc} alt={article.title} className="w-full h-auto max-h-[480px] object-cover object-top" />
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap">{tag}</span>
              ))}
            </div>

            <div className="mt-10 prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: `<style>
              .prose h2 { font-family: var(--font-heading); font-size: 1.5rem; margin-top: 2.5rem; margin-bottom: 1rem; color: oklch(var(--foreground-900)); }
              .prose p { color: oklch(var(--foreground-700)); line-height: 1.85; margin-bottom: 1.25rem; font-size: 1.05rem; }
              .prose strong { color: oklch(var(--foreground-900)); }
            </style>${article.contentHtml}`}} />

            <div className="mt-16 pt-10 border-t border-background-200">
              <Link to={`/category/${article.categorySlug}`} className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors">
                <i className="ri-arrow-left-line"></i> 返回 {article.categoryName}
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => {
                  const raImg = articleImages[ra.slug] || '';
                  return (
                    <Link key={ra.id} to={`/article/${ra.slug}`} className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300/50 transition-all duration-300">
                      <div className="relative w-full h-40 overflow-hidden">
                        {raImg && <img src={raImg} alt={ra.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />}
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-heading text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">{ra.title}</h3>
                        <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                        <div className="mt-3 text-xs text-foreground-400">{ra.date}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}